import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminTenantSubscriptionsService } from "@/services/admin-tenant-subscriptions-service";
import { tenantCatalogKeys } from "./useTenants";

export const tenantSubscriptionsKeys = {
  all: ["tenantSubscriptions"] as const,
  lists: () => [...tenantSubscriptionsKeys.all, "list"] as const,
  list: (tenantId: string) =>
    [...tenantSubscriptionsKeys.lists(), tenantId] as const,
  merged: (tenantId: string) =>
    [...tenantSubscriptionsKeys.all, "merged", tenantId] as const,
};

export const useTenantSubscriptions = (
  tenantId: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: tenantId
      ? tenantSubscriptionsKeys.list(tenantId)
      : tenantSubscriptionsKeys.lists(),
    queryFn: () =>
      adminTenantSubscriptionsService.getTenantSubscriptions(tenantId ?? ""),
    staleTime: 60 * 1000,
    enabled: enabled && tenantId !== null,
  });

export const useTenantMergedFeatures = (
  tenantId: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: tenantId
      ? tenantSubscriptionsKeys.merged(tenantId)
      : [...tenantSubscriptionsKeys.all, "merged"],
    queryFn: () =>
      adminTenantSubscriptionsService.getTenantMergedFeatures(tenantId ?? ""),
    staleTime: 60 * 1000,
    enabled: enabled && tenantId !== null,
  });

export const useAssignTenantSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, planId }: { tenantId: string; planId: string }) =>
      adminTenantSubscriptionsService.assignPlan(tenantId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: tenantSubscriptionsKeys.list(variables.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: tenantSubscriptionsKeys.merged(variables.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: tenantCatalogKeys.details(),
      });
      queryClient.invalidateQueries({
        queryKey: tenantCatalogKeys.lists(),
      });
    },
  });
};

export const useCancelTenantSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      subscriptionId,
      cancelAtPeriodEnd,
    }: {
      tenantId: string;
      subscriptionId: string;
      cancelAtPeriodEnd: boolean;
    }) =>
      adminTenantSubscriptionsService.cancelSubscription(
        tenantId,
        subscriptionId,
        cancelAtPeriodEnd,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: tenantSubscriptionsKeys.list(variables.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: tenantSubscriptionsKeys.merged(variables.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: tenantCatalogKeys.details(),
      });
      queryClient.invalidateQueries({
        queryKey: tenantCatalogKeys.lists(),
      });
    },
  });
};
